/* Hauser MX — micro-motor WebGL2 (Fase 8).
 *
 * Todo el trabajo de shader sobre FOTOGRAFÍA vive aquí: las transiciones
 * líquidas del hero, el hover de las tarjetas y el visor de la ficha. Son un
 * plano a pantalla completa con dos texturas; eso no justifica cargar una
 * librería 3D de casi un mega, así que está escrito a mano. Three.js se
 * reserva para las escenas que sí son geometría 3D real (la llave).
 *
 * Lo que hace el shader, y por qué:
 *   · Barrido diagonal con el borde perturbado por ruido simplex — una
 *     cortina recta se lee como "plantilla"; un borde que respira se lee como
 *     que el material tiene textura.
 *   · Desplazamiento de las UV proporcional a lo lejos que esté la transición
 *     de sus extremos: máximo a la mitad, cero al principio y al final. Es lo
 *     que produce la sensación de líquido en vez de fundido.
 *   · Aberración cromática solo durante el cambio, apagándose igual. Da el
 *     grano óptico de una lente real; si estuviera siempre encendida se vería
 *     como un defecto.
 *   · Parallax suave por puntero: la foto se mueve DENTRO de su marco, sin
 *     mover el marco. Refuerza la profundidad sin desplazar el layout.
 *
 * Reglas de convivencia:
 *   · El <img> original NUNCA se quita del DOM. El canvas se superpone. Así
 *     el HTML sigue siendo correcto para Google y para quien no tenga WebGL,
 *     y si algo falla aquí simplemente se ve la foto normal.
 *   · Fuera de pantalla no se dibuja ni un cuadro.
 *   · En nivel `flat` este archivo no crea nada: manda el CSS de la fase 4.
 */
(function () {
  'use strict';

  var H = window.Hauser;
  if (!H) return;

  /* ── Shaders ───────────────────────────────────────────────────────── */

  var VERT = [
    '#version 300 es',
    'in vec2 aPos;',
    'out vec2 vUv;',
    'void main(){',
    '  vUv = aPos * 0.5 + 0.5;',
    '  gl_Position = vec4(aPos, 0.0, 1.0);',
    '}'
  ].join('\n');

  /* Ruido simplex 2D (implementación clásica de Ashima/Gustavson, dominio
     público). Se usa para deformar el borde de la transición y para el grano.
     Es más caro que un value-noise pero no tiene los artefactos de rejilla,
     que sobre una fotografía se notan muchísimo. */
  var NOISE = [
    'vec3 mod289(vec3 x){return x-floor(x*(1.0/289.0))*289.0;}',
    'vec2 mod289(vec2 x){return x-floor(x*(1.0/289.0))*289.0;}',
    'vec3 permute(vec3 x){return mod289(((x*34.0)+1.0)*x);}',
    'float snoise(vec2 v){',
    '  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);',
    '  vec2 i  = floor(v + dot(v, C.yy));',
    '  vec2 x0 = v - i + dot(i, C.xx);',
    '  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);',
    '  vec4 x12 = x0.xyxy + C.xxzz;',
    '  x12.xy -= i1;',
    '  i = mod289(i);',
    '  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));',
    '  vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);',
    '  m = m*m; m = m*m;',
    '  vec3 x = 2.0 * fract(p * C.www) - 1.0;',
    '  vec3 h = abs(x) - 0.5;',
    '  vec3 ox = floor(x + 0.5);',
    '  vec3 a0 = x - ox;',
    '  m *= 1.79284291400159 - 0.85373472095314 * (a0*a0 + h*h);',
    '  vec3 g;',
    '  g.x  = a0.x  * x0.x  + h.x  * x0.y;',
    '  g.yz = a0.yz * x12.xz + h.yz * x12.yw;',
    '  return 130.0 * dot(m, g);',
    '}'
  ].join('\n');

  var FRAG = [
    '#version 300 es',
    'precision highp float;',
    'in vec2 vUv;',
    'out vec4 fragColor;',
    'uniform sampler2D uTex0;',
    'uniform sampler2D uTex1;',
    'uniform vec2  uRes;',
    'uniform vec2  uSize0;',
    'uniform vec2  uSize1;',
    'uniform float uProgress;',
    'uniform float uTime;',
    'uniform vec2  uPuntero;',
    'uniform float uParallax;',
    'uniform float uIntensidad;',
    'uniform float uGrano;',
    NOISE,
    /* Encaje tipo `object-fit: cover` hecho a mano: sin esto la foto se
       estira, que es exactamente lo que el diseño editorial no perdona. */
    'vec2 cubrir(vec2 uv, vec2 res, vec2 img){',
    '  if (img.x < 1.0 || img.y < 1.0) return uv;',
    '  float ar_c = res.x / res.y;',
    '  float ar_i = img.x / img.y;',
    '  vec2 s = ar_i > ar_c ? vec2(ar_c / ar_i, 1.0) : vec2(1.0, ar_i / ar_c);',
    '  return (uv - 0.5) * s + 0.5;',
    '}',
    'void main(){',
    '  vec2 uv = vUv;',
    /* Parallax: se amplía un pelo y se desplaza contra el puntero. El 0.5 de
       amortiguación evita que en los bordes se descubra el fondo. */
    '  vec2 par = uPuntero * uParallax;',
    '  uv = (uv - 0.5) * (1.0 - uParallax * 0.5) + 0.5 + par;',
    '',
    '  float p = clamp(uProgress, 0.0, 1.0);',
    /* Campana: 0 en los extremos, 1 a la mitad. Gobierna cuánta distorsión y
       cuánta aberración hay en cada momento. */
    '  float campana = sin(p * 3.14159265) * uIntensidad;',
    '',
    '  float n  = snoise(uv * 2.6 + vec2(uTime * 0.04, 0.0));',
    '  float n2 = snoise(uv * 3.4 - vec2(0.0, uTime * 0.05) + 41.7);',
    '',
    /* Barrido diagonal: el frente avanza de abajo-izquierda a arriba-derecha
       y el ruido le mueve el borde. El rango va de -0.25 a 1.25 para que a
       p=0 y p=1 el barrido esté completamente fuera de la imagen. */
    '  float d = uv.x * 0.72 + uv.y * 0.28;',
    '  float frente = p * 1.5 - 0.25 + n * 0.11;',
    '  float mezcla = smoothstep(frente - 0.16, frente + 0.16, d);',
    '  mezcla = 1.0 - mezcla;',
    '',
    '  vec2 disp = vec2(n, n2) * 0.075 * campana;',
    '  vec2 uv0 = cubrir(uv + disp, uRes, uSize0);',
    '  vec2 uv1 = cubrir(uv - disp, uRes, uSize1);',
    '',
    /* Aberración: los tres canales se muestrean con un pequeño corrimiento a
       lo largo de la dirección del barrido. */
    '  vec2 dir = normalize(vec2(0.72, 0.28));',
    '  float ca = 0.0075 * campana;',
    '  vec3 c0 = vec3(',
    '    texture(uTex0, uv0 + dir * ca).r,',
    '    texture(uTex0, uv0).g,',
    '    texture(uTex0, uv0 - dir * ca).b);',
    '  vec3 c1 = vec3(',
    '    texture(uTex1, uv1 + dir * ca).r,',
    '    texture(uTex1, uv1).g,',
    '    texture(uTex1, uv1 - dir * ca).b);',
    '',
    '  vec3 col = mix(c0, c1, mezcla);',
    '',
    /* Realce del propio frente: una lámina de luz cálida justo en el borde.
       Es lo que hace que se lea como un material que se despliega y no como
       dos fotos intercambiándose. */
    '  float luz = smoothstep(0.12, 0.0, abs(d - frente)) * campana;',
    '  col += vec3(0.91, 0.78, 0.48) * luz * 0.16;',
    '',
    /* Grano finísimo, ligado al tiempo. Unifica las fotos (que vienen de
       cámaras distintas) y esconde el banding de los degradados. */
    '  float g = snoise(uv * uRes.xy * 0.55 + uTime * 60.0) * 0.5 + 0.5;',
    '  col += (g - 0.5) * uGrano;',
    '',
    '  fragColor = vec4(col, 1.0);',
    '}'
  ].join('\n');

  /* ── Utilidades de GL ──────────────────────────────────────────────── */

  function compilar(gl, tipo, fuente) {
    var sh = gl.createShader(tipo);
    gl.shaderSource(sh, fuente);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      if (window.console) console.warn('[Hauser GL]', gl.getShaderInfoLog(sh));
      gl.deleteShader(sh);
      return null;
    }
    return sh;
  }

  function programa(gl) {
    var vs = compilar(gl, gl.VERTEX_SHADER, VERT);
    var fs = compilar(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return null;
    var pr = gl.createProgram();
    gl.attachShader(pr, vs);
    gl.attachShader(pr, fs);
    gl.linkProgram(pr);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) {
      if (window.console) console.warn('[Hauser GL]', gl.getProgramInfoLog(pr));
      return null;
    }
    return pr;
  }

  function cargarImagen(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      /* El CDN de Shopify responde `access-control-allow-origin: *`
         (verificado), así que la textura no queda "tainted". Sin esto
         `texImage2D` lanza un error de seguridad y se cae toda la escena. */
      img.crossOrigin = 'anonymous';
      img.decoding = 'async';
      img.onload = function () {
        resolve(img);
      };
      img.onerror = function () {
        reject(new Error('no se pudo cargar ' + src));
      };
      img.src = src;
    });
  }

  /* ── La escena ─────────────────────────────────────────────────────── */

  /**
   * @param {HTMLElement} contenedor  Elemento que ya tiene el <img> visible.
   * @param {Object} opciones
   *   fuentes    {string[]} URLs de las fotos (mínimo 1).
   *   parallax   {number}   0 = sin parallax. Por defecto 0.03.
   *   intensidad {number}   multiplicador de distorsión. Por defecto 1.
   *   grano      {number}   0..0.1. Por defecto 0.02.
   *   autoplay   {number}   ms entre fotos; 0 = manual.
   *   perezoso   {boolean}  Carga las texturas BAJO DEMANDA en vez de todas de
   *              golpe. Imprescindible en la galería de la ficha: una
   *              propiedad tiene 18-24 fotos y subirlas todas a la GPU son
   *              ~40 MB de memoria de video, que en un teléfono de gama media
   *              provoca que el navegador tire el contexto sin avisar. Con
   *              esto solo viven en memoria la foto actual y sus vecinas.
   */
  function EscenaGL(contenedor, opciones) {
    this.el = contenedor;
    this.op = Object.assign(
      { fuentes: [], parallax: 0.03, intensidad: 1, grano: 0.02, autoplay: 0, perezoso: false },
      opciones || {}
    );
    this.indice = 0;
    this.progreso = 0;
    this.texturas = [];
    this.tamanos = [];
    this.visible = false;
    this.muerta = false;
    this.tiempo = 0;
    this._quitarObs = null;
    this._quitarLoop = null;

    this._montar();
  }

  EscenaGL.prototype._montar = function () {
    var self = this;
    if (H.tier === 'flat' || this.op.fuentes.length < 1) return;

    var canvas = document.createElement('canvas');
    canvas.className = 'hauser-gl__canvas';
    canvas.setAttribute('aria-hidden', 'true');
    this.canvas = canvas;

    var gl = canvas.getContext('webgl2', {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      /* La foto ya está pintada debajo; no hace falta conservar el buffer. */
      preserveDrawingBuffer: false,
      powerPreference: H.tier === 'full' ? 'high-performance' : 'low-power'
    });
    if (!gl) return;
    this.gl = gl;

    var pr = programa(gl);
    if (!pr) return;
    this.pr = pr;
    gl.useProgram(pr);

    /* Un triángulo que cubre la pantalla — más barato que dos y sin la
       costura diagonal del quad. */
    var vao = gl.createVertexArray();
    gl.bindVertexArray(vao);
    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(pr, 'aPos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    this.u = {};
    ['uTex0', 'uTex1', 'uRes', 'uSize0', 'uSize1', 'uProgress', 'uTime', 'uPuntero', 'uParallax', 'uIntensidad', 'uGrano'].forEach(
      function (n) {
        self.u[n] = gl.getUniformLocation(pr, n);
      }
    );

    gl.uniform1i(this.u.uTex0, 0);
    gl.uniform1i(this.u.uTex1, 1);
    gl.uniform1f(this.u.uParallax, H.mobile ? this.op.parallax * 0.4 : this.op.parallax);
    gl.uniform1f(this.u.uIntensidad, H.tier === 'lite' ? this.op.intensidad * 0.6 : this.op.intensidad);
    gl.uniform1f(this.u.uGrano, this.op.grano);

    /* Solo se dibuja cuando se ve. Un canvas de hero corriendo a 60fps fuera
       de pantalla es batería tirada a la basura, y en una laptop se oye. */
    this._quitarObs = H.enVista(
      this.el,
      function (dentro) {
        self.visible = dentro;
        if (dentro) self._arrancarLoop();
        else self._pararLoop();
      },
      { once: false, rootMargin: '100px 0px' }
    );

    this._cargarTexturas();
  };

  /* Sube UNA imagen ya cargada a la ranura `i`. */
  EscenaGL.prototype._subir = function (i, img) {
    var gl = this.gl;
    /* WebGL pone el origen de la textura abajo-izquierda y el <img> arriba-
       izquierda: sin esto las fotos salen boca abajo. */
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    var t = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
    this.texturas[i] = t;
    this.tamanos[i] = [img.naturalWidth, img.naturalHeight];
  };

  /* Garantiza que la textura `i` existe. Devuelve una promesa. En modo no
     perezoso ya están todas, así que resuelve de inmediato. */
  EscenaGL.prototype.asegurar = function (i) {
    var self = this;
    if (this.muerta) return Promise.resolve(false);
    if (this.texturas[i]) return Promise.resolve(true);
    var src = this.op.fuentes[i];
    if (!src) return Promise.resolve(false);

    this._pendientes = this._pendientes || {};
    if (this._pendientes[i]) return this._pendientes[i];

    this._pendientes[i] = cargarImagen(src)
      .then(function (img) {
        if (self.muerta) return false;
        self._subir(i, img);
        return true;
      })
      .catch(function () {
        return false;
      })
      .then(function (ok) {
        delete self._pendientes[i];
        return ok;
      });

    return this._pendientes[i];
  };

  /* Suelta las texturas lejanas al índice actual. Sin esto, recorrer 24 fotos
     acaba con las 24 en memoria de video, que es justo lo que el modo perezoso
     venía a evitar. */
  EscenaGL.prototype._podar = function (centro, radio) {
    if (!this.op.perezoso) return;
    var gl = this.gl;
    var n = this.op.fuentes.length;
    for (var i = 0; i < n; i++) {
      if (!this.texturas[i]) continue;
      /* Distancia circular: en una galería que da la vuelta, la última foto es
         vecina de la primera. */
      var d = Math.min(Math.abs(i - centro), n - Math.abs(i - centro));
      if (d > radio) {
        gl.deleteTexture(this.texturas[i]);
        this.texturas[i] = null;
        this.tamanos[i] = null;
      }
    }
  };

  EscenaGL.prototype._cargarTexturas = function () {
    var self = this;

    /* Modo perezoso: solo la primera y la siguiente. El resto llega cuando se
       pidan, y las lejanas se sueltan (ver `_podar`). */
    var iniciales = this.op.perezoso
      ? [0, Math.min(1, this.op.fuentes.length - 1)]
      : this.op.fuentes.map(function (_, i) { return i; });

    Promise.all(
      iniciales.map(function (i) {
        return cargarImagen(self.op.fuentes[i])
          .then(function (img) { return { i: i, img: img }; })
          .catch(function () { return null; });
      })
    ).then(function (res) {
      if (self.muerta) return;
      var validas = res.filter(Boolean);
      /* Si no cargó ni una, no se monta el canvas y se queda la foto del DOM:
         degradación silenciosa, nunca un rectángulo negro. */
      if (!validas.length) return;

      validas.forEach(function (r) { self._subir(r.i, r.img); });

      self.el.appendChild(self.canvas);
      self.el.setAttribute('data-hauser-gl', 'activo');
      self._medir();
      self._enlazarResize();
      self._aplicarPar(0, self.texturas[1] ? 1 : 0);
      if (self.visible) self._arrancarLoop();
      self.dibujar();

      if (self.op.autoplay > 0 && self.op.fuentes.length > 1) self._autoplay();
    });
  };

  EscenaGL.prototype._enlazarResize = function () {
    var self = this;
    if (!('ResizeObserver' in window)) return;
    this._ro = new ResizeObserver(function () {
      self._medir();
      if (!self._loop) self.dibujar();
    });
    this._ro.observe(this.el);
  };

  EscenaGL.prototype._medir = function () {
    var r = this.el.getBoundingClientRect();
    var dpr = H.dpr;
    var w = Math.max(1, Math.round(r.width * dpr));
    var h = Math.max(1, Math.round(r.height * dpr));
    if (this.canvas.width === w && this.canvas.height === h) return;
    this.canvas.width = w;
    this.canvas.height = h;
    this.gl.viewport(0, 0, w, h);
    this.gl.useProgram(this.pr);
    this.gl.uniform2f(this.u.uRes, w, h);
  };

  /* Deja las texturas `a` y `b` en las unidades 0 y 1. */
  EscenaGL.prototype._aplicarPar = function (a, b) {
    var gl = this.gl;
    if (!this.texturas[a] || !this.texturas[b]) return;
    gl.useProgram(this.pr);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.texturas[a]);
    gl.uniform2f(this.u.uSize0, this.tamanos[a][0], this.tamanos[a][1]);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.texturas[b]);
    gl.uniform2f(this.u.uSize1, this.tamanos[b][0], this.tamanos[b][1]);
    this._par = [a, b];
  };

  EscenaGL.prototype.dibujar = function () {
    if (this.muerta || !this.gl || !this.texturas.length) return;
    var gl = this.gl;
    gl.useProgram(this.pr);
    gl.uniform1f(this.u.uProgress, this.progreso);
    gl.uniform1f(this.u.uTime, this.tiempo);
    gl.uniform2f(this.u.uPuntero, this._px || 0, this._py || 0);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  };

  EscenaGL.prototype._arrancarLoop = function () {
    var self = this;
    if (this._loop || this.muerta) return;
    this._loop = true;
    this._quitarLoop = H.porCuadro(function (t, dt) {
      self.tiempo += dt / 1000;
      /* Parallax solo con puntero fino y solo si el ratón está sobre este
         elemento; con puntero grueso el uniform se queda en 0 y el shader
         hace exactamente lo mismo sin ninguna rama extra. */
      if (self._hover) {
        self._px = H.puntero.scx * 0.5;
        self._py = H.puntero.scy * 0.5;
      } else {
        self._px = (self._px || 0) * 0.92;
        self._py = (self._py || 0) * 0.92;
      }
      self.dibujar();
    });
  };

  EscenaGL.prototype._pararLoop = function () {
    if (this._quitarLoop) this._quitarLoop();
    this._quitarLoop = null;
    this._loop = false;
  };

  /* Transición animada hacia el índice `i`. Devuelve el tween para poder
     encadenarlo o matarlo desde fuera. */
  EscenaGL.prototype.ir = function (i, duracion) {
    var self = this;
    var n = this.op.fuentes.length;
    if (this.muerta || !n) return null;
    i = ((i % n) + n) % n;
    if (i === this.indice) return null;

    /* En modo perezoso la textura destino puede no existir todavía: se espera
       a tenerla ANTES de arrancar la transición. Si se lanzara sin ella, el
       shader mezclaría contra una textura vacía y se vería un fundido a negro
       — peor que esperar 150 ms. */
    this.asegurar(i).then(function (ok) {
      if (self.muerta || !ok) return;
      var gsap = window.gsap;

      self._aplicarPar(self.indice, i);
      self.progreso = 0;

      if (!gsap) {
        self.progreso = 1;
        self.indice = i;
        self.dibujar();
        self._podar(i, 2);
        return;
      }

      if (self._tween) self._tween.kill();
      self._tween = gsap.to(self, {
        progreso: 1,
        duration: duracion || (H.mobile ? 0.9 : 1.15),
        ease: 'power2.inOut',
        onComplete: function () {
          self.indice = i;
          /* Se deja el par [i, i] para que en reposo el shader muestre la foto
             final sin depender de que `progreso` se quede exactamente en 1. */
          self._aplicarPar(i, i);
          self.progreso = 0;
          self.dibujar();
          /* Precargar las vecinas y soltar las lejanas. */
          self.asegurar((i + 1) % n);
          self.asegurar((i - 1 + n) % n);
          self._podar(i, 2);
        }
      });
    });

    return null;
  };

  /* Progreso manual (lo usa el hero atado al scroll). */
  EscenaGL.prototype.fijar = function (desde, hasta, p) {
    if (this.muerta || !this.texturas.length) return;
    if (!this._par || this._par[0] !== desde || this._par[1] !== hasta) {
      this._aplicarPar(desde, hasta);
    }
    this.progreso = p;
    if (!this._loop) this.dibujar();
  };

  EscenaGL.prototype._autoplay = function () {
    var self = this;
    var siguiente = function () {
      if (self.muerta) return;
      if (self.visible && !self._pausado) self.ir(self.indice + 1);
      self._timer = setTimeout(siguiente, self.op.autoplay);
    };
    this._timer = setTimeout(siguiente, this.op.autoplay);
  };

  /* Pausa/reanuda el avance automático sin destruir el temporizador: lo usa la
     galería para detener el "recorrido" al tocar cualquier control. */
  EscenaGL.prototype.pausar = function (v) {
    this._pausado = !!v;
  };

  EscenaGL.prototype.hover = function (dentro) {
    this._hover = dentro;
  };

  EscenaGL.prototype.destruir = function () {
    this.muerta = true;
    this._pararLoop();
    if (this._timer) clearTimeout(this._timer);
    if (this._quitarObs) this._quitarObs();
    if (this._ro) this._ro.disconnect();
    if (this._tween) this._tween.kill();
    var gl = this.gl;
    if (gl) {
      /* En modo perezoso el array tiene huecos (texturas ya podadas), así que
         hay que saltarlos: `deleteTexture(null)` no falla pero ensucia. */
      this.texturas.forEach(function (t) {
        if (t) gl.deleteTexture(t);
      });
      /* Los contextos WebGL están limitados por pestaña (~16). Un catálogo
         con 30 tarjetas los agota y el navegador empieza a matar los más
         viejos en silencio; por eso se liberan explícitamente. */
      var ext = gl.getExtension('WEBGL_lose_context');
      if (ext) ext.loseContext();
    }
    if (this.canvas && this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas);
    this.el.removeAttribute('data-hauser-gl');
  };

  window.HauserGL = {
    Escena: EscenaGL,
    disponible: function () {
      return H.tier !== 'flat';
    }
  };
})();
